/**
 * 线拖拽策略。
 * 职责：
 * 1. 实现草图线对象的拖拽行为；
 * 2. 通过射线与参考平面求交计算拖拽位移；
 * 3. 整体平移线段的起点与终点。
 */

import * as THREE from 'three';
import {
    isSketchCollectionObject,
    SketchLineObject,
    type SketchPointObject,
} from '../../mesh/sketch';
import type {Viewer} from '../../viewer/Viewer';
import type {RawPointerEventPayload} from '../../viewer/moudles/EventManager';
import type {SketchParametricDragSession} from '../../sketch/solver/runtime/SketchParametricDragSession';
import {AbstractEntityDragStrategy, EntityDragStrategyType} from './AbstractEntityDragStrategy';

/**
 * 判断对象是否为线拖拽策略。
 * @param value 待判断的拖拽策略对象。
 * @returns 当对象为线拖拽策略时返回 true。
 */
export function isLineDragStrategy(value: AbstractEntityDragStrategy): value is LineDragStrategy {
    return value.strategyType === EntityDragStrategyType.Line;
}

/**
 * 线拖拽策略。
 */
export class LineDragStrategy extends AbstractEntityDragStrategy {
    /** 策略类型标识。 */
    public readonly strategyType = EntityDragStrategyType.Line;

    /** 当前拖拽中的线对象。 */
    private readonly line: SketchLineObject;

    /** 线拖拽参考平面。 */
    private dragPlane: THREE.Plane | null = null;

    /** pointerdown 时的真实命中点，全部预演都从该不可变基线计算。 */
    private readonly dragStartWorldPoint = new THREE.Vector3();

    /** pointerdown 时的直线起点世界坐标。 */
    private readonly startPointBaseline = new THREE.Vector3();

    /** pointerdown 时的直线终点世界坐标。 */
    private readonly endPointBaseline = new THREE.Vector3();

    /** 当前鼠标对应的绝对平移量。 */
    private readonly requestedTranslation = new THREE.Vector3();

    /** 起点参数目标热路径复用向量。 */
    private readonly startPointTarget = new THREE.Vector3();

    /** 终点参数目标热路径复用向量。 */
    private readonly endPointTarget = new THREE.Vector3();

    /** 两端点统一参数意图使用的稳定映射。 */
    private readonly pointTargets = new Map<SketchPointObject, THREE.Vector3>();

    /** 射线求交热路径复用向量。 */
    private readonly intersectionWorld = new THREE.Vector3();

    /** 是否已经记录有效的最终鼠标目标。 */
    private hasRequestedTranslation = false;

    /** pointerdown 时捕获不可变求解岛的参数拖拽会话。 */
    private parametricDragSession: SketchParametricDragSession | null = null;

    /**
     * 构造函数。
     * @param renderer 渲染器实例。
     * @param line 当前拖拽的线对象。
     */
    constructor(renderer: Viewer, line: SketchLineObject) {
        super(renderer);
        this.line = line;
        this.pointTargets.set(line.startPoint, this.startPointTarget);
        this.pointTargets.set(line.endPoint, this.endPointTarget);
    }

    /**
     * 获取当前拖拽的线对象。
     * @returns 拖拽中的线对象。
     */
    public getLine(): SketchLineObject {
        return this.line;
    }

    /**
     * 开始线拖拽。
     * 初始化参考平面、记录基准点并启动留影。
     */
    public start(payload: RawPointerEventPayload): void {
        this.resetAdaptiveParametricPreviewState();
        this.hasRequestedTranslation = false;
        this.parametricDragSession?.dispose();
        this.parametricDragSession = null;

        // 以线段中点位置构建参考平面。
        const midpoint = this.line.getMidpointPosition();
        this.dragPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(
            this.line.referencePlane.normal.clone(),
            midpoint,
        );

        // 真实按下点决定绝对平移基线，禁止用中点替代后让第一帧发生跳变。
        const pointerWorld = this.resolvePointerWorldPosition(payload);
        if (!pointerWorld) {
            this.dragPlane = null;
            return;
        }
        this.dragStartWorldPoint.copy(pointerWorld);
        this.line.startPoint.getPositionTo(this.startPointBaseline);
        this.line.endPoint.getPositionTo(this.endPointBaseline);

        const previewOwner = this.resolveDragPreviewOwner();
        this.dragPreviewOwner = previewOwner;
        this.beginSketchRuntimeHistorySession('移动草图直线', 'sketch.runtime.drag.line');
        this.parametricDragSession = this.renderer.beginActiveSketchParametricDragSession([
            this.line.startPoint,
            this.line.endPoint,
        ]);
        if (!this.parametricDragSession) {
            this.cancelSketchRuntimeHistorySession();
            this.dragPreviewOwner = null;
            this.dragPlane = null;
            return;
        }
        try {
            this.invokeDragPreviewBegin(previewOwner);
        } catch (error) {
            this.cancelSketchRuntimeHistorySession();
            this.parametricDragSession.dispose();
            this.parametricDragSession = null;
            this.dragPreviewOwner = null;
            this.dragPlane = null;
            throw error;
        }
        this.disableCameraControlsForDrag();
    }

    /**
     * 更新线拖拽位置。
     * @param payload 原始鼠标事件载荷。
     */
    public update(payload: RawPointerEventPayload): void {
        if (!this.dragPlane || !this.parametricDragSession) {
            return;
        }

        // 左键释放时立即终止。
        if ((payload.buttons & 1) !== 1) {
            return;
        }

        const intersection = this.resolvePointerWorldPosition(payload);
        if (!intersection) {
            return;
        }

        // 每帧只更新绝对目标；求解器始终从 pointerdown 的同一份 island 基线出发。
        this.requestedTranslation.copy(intersection).sub(this.dragStartWorldPoint);
        this.hasRequestedTranslation = true;
        const applied = this.runAdaptiveParametricPreview(() => this.applyRequestedTranslation());
        if (applied === true) {
            this.markGeometryChanged();
        }

        payload.nativeEvent.preventDefault();
    }

    /**
     * 子类具体停止逻辑。
     */
    protected doStop(): void {
        try {
            // 外部事务已替换草图时，旧基线不再具备写回 authority，必须直接放弃整个手势。
            if (this.discardStaleSketchDrag(this.parametricDragSession)) {
                return;
            }
            /**
             * 仅补算被节流的最终绝对目标；普通失败帧可在提交前重新验证同一目标，
             * stale 帧已在上方永久拒绝。
             */
            let applied = this.flushPendingParametricPreview();
            if (applied === null
                && this.hasRequestedTranslation
                && !this.parametricDragSession?.canReuseLastAppliedFrame()) {
                applied = this.applyRequestedTranslation();
            }
            if (applied === true) {
                this.markGeometryChanged();
            }
            // pending 补帧也可能观察到外部版本变化，提交历史前必须再次确认 authority。
            if (this.discardStaleSketchDrag(this.parametricDragSession)) {
                return;
            }
            this.commitSketchRuntimeHistorySession();
        } finally {
            this.parametricDragSession?.dispose();
            this.parametricDragSession = null;
            this.dragPlane = null;
            this.hasRequestedTranslation = false;
            this.resetAdaptiveParametricPreviewState();
        }
    }

    /**
     * 使用当前绝对平移目标统一预演直线两个端点。
     * @returns 关系、尺寸与实体结构方程全部收敛并原子写回时返回 true。
     */
    private applyRequestedTranslation(): boolean {
        if (!this.parametricDragSession) {
            return false;
        }
        this.startPointTarget.copy(this.startPointBaseline).add(this.requestedTranslation);
        this.endPointTarget.copy(this.endPointBaseline).add(this.requestedTranslation);
        return this.renderer.previewSketchParametricPointTargets(
            this.pointTargets,
            `line-translate:${this.line.uuid}`,
            this.parametricDragSession,
        );
    }

    /**
     * 解析指针在当前直线拖拽平面上的世界坐标。
     * @param payload 原始指针载荷。
     * @returns 射线有效命中时返回复用向量，否则返回 null。
     */
    private resolvePointerWorldPosition(payload: RawPointerEventPayload): THREE.Vector3 | null {
        if (!this.dragPlane || !this.renderer.camera
            || !this.updateMouseNdc(payload.clientX, payload.clientY)) {
            return null;
        }
        this.raycaster.setFromCamera(this.mouseNdc, this.renderer.camera);
        return this.raycaster.ray.intersectPlane(this.dragPlane, this.intersectionWorld);
    }

    /**
     * 解析拖拽留影承载对象。
     * 优先使用封闭线段几何对象，否则使用线对象自身。
     * @returns 承载留影的对象。
     */
    private resolveDragPreviewOwner(): THREE.Object3D {
        // 优先使用直接关联的封闭线段几何对象。
        if (this.line.closedCollection && isSketchCollectionObject(this.line.closedCollection)) {
            return this.line.closedCollection;
        }

        // 向上查找祖先中的线段几何对象。
        let current: THREE.Object3D | null = this.line;
        while (current) {
            if (isSketchCollectionObject(current)) {
                return current;
            }
            current = current.parent;
        }

        return this.line;
    }
}
